// ============================================================================
// DEPTH CHART — Starting Lineup, Minutes, Position Management
// ============================================================================
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { getPlayerRating, getRatingColor } from './playerRatings';
import PLAYER_POSITIONS from './playerPositions';

const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C'];
const POS_INDEX = { PG: 0, SG: 1, SF: 2, PF: 3, C: 4 };
const DEPTH_LABELS = ['Starter', '6th Man / Backup', 'Reserve'];

// ============================================================================
// OUT-OF-POSITION PENALTY
// ============================================================================
// PG(0) → SG(1) → SF(2) → PF(3) → C(4)
// Distance 0: natural, no penalty
// Distance 1: adjacent (PG↔SG, SF↔PF) = -1 OVR
// Distance 2: stretch (PG↔SF, SG↔PF, SF↔C) = -3 OVR
// Distance 3: misfit (PG↔PF, SG↔C) = -4 OVR
// Distance 4: disaster (PG↔C) = -5 OVR
export function getOOPPenalty(naturalPos, assignedPos) {
  if (!naturalPos || !assignedPos) return 0;
  const natPositions = naturalPos.split('/').map(p => p.trim());
  let bestPenalty = 99;
  for (const nat of natPositions) {
    const natIdx = POS_INDEX[nat];
    const assignIdx = POS_INDEX[assignedPos];
    if (natIdx === undefined || assignIdx === undefined) { bestPenalty = Math.min(bestPenalty, 0); continue; }
    const dist = Math.abs(natIdx - assignIdx);
    const penalties = [0, 1, 3, 4, 5]; // positive values (penalties)
    const pen = dist < penalties.length ? penalties[dist] : 5;
    bestPenalty = Math.min(bestPenalty, pen);
  }
  return -bestPenalty;
}

export function getEffectiveOVR(playerName, assignedPos) {
  const baseOvr = getPlayerRating(playerName) || 70;
  const naturalPos = PLAYER_POSITIONS[playerName] || 'SF';
  const penalty = getOOPPenalty(naturalPos, assignedPos);
  return Math.max(40, baseOvr + penalty);
}

// ============================================================================
// AUTO-GENERATE DEPTH CHART from roster
// ============================================================================
export function generateDefaultDepthChart(roster) {
  if (!roster || roster.length === 0) return null;

  // Get all active players with ratings
  const players = roster
    .filter(p => !p.isDead && (p.salary2526 || 0) > 0)
    .map(p => ({
      name: p.name,
      ovr: p.ovr || getPlayerRating(p.name) || 70,
      naturalPos: PLAYER_POSITIONS[p.name] || 'SF',
      isTwoWay: p.isTwoWay || false,
    }))
    .sort((a, b) => b.ovr - a.ovr);

  const chart = {};
  const assigned = new Set();

  // Pass 1: Fill starters with natural-position players (best OVR first)
  for (const pos of POSITIONS) {
    chart[pos] = [];
    const best = players.find(p =>
      !assigned.has(p.name) && !p.isTwoWay &&
      p.naturalPos.split('/').some(np => np.trim() === pos)
    );
    if (best) {
      chart[pos].push({ name: best.name, minutes: 32 });
      assigned.add(best.name);
    }
  }

  // Pass 2: Fill any empty starter slots with best remaining
  for (const pos of POSITIONS) {
    if (chart[pos].length === 0) {
      const best = players.find(p => !assigned.has(p.name) && !p.isTwoWay);
      if (best) {
        chart[pos].push({ name: best.name, minutes: 28 });
        assigned.add(best.name);
      }
    }
  }

  // Pass 3: Fill backups (best remaining at or near position)
  for (const pos of POSITIONS) {
    const posIdx = POS_INDEX[pos];
    const backup = players.find(p => {
      if (assigned.has(p.name) || p.isTwoWay) return false;
      const natPositions = p.naturalPos.split('/').map(np => np.trim());
      return natPositions.some(np => Math.abs((POS_INDEX[np] || 2) - posIdx) <= 1);
    }) || players.find(p => !assigned.has(p.name) && !p.isTwoWay);
    if (backup) {
      chart[pos].push({ name: backup.name, minutes: 12 });
      assigned.add(backup.name);
    }
  }

  // Pass 4: Fill reserves with remaining players
  for (const pos of POSITIONS) {
    if (chart[pos].length < 3) {
      const reserve = players.find(p => !assigned.has(p.name) && !p.isTwoWay);
      if (reserve) {
        chart[pos].push({ name: reserve.name, minutes: 4 });
        assigned.add(reserve.name);
      }
    }
  }

  // Add unassigned players to DNP
  const dnp = players.filter(p => !assigned.has(p.name)).map(p => p.name);

  return { positions: chart, dnp };
}

// ============================================================================
// DEPTH CHART COMPONENT
// ============================================================================
const DepthChart = ({ teamAbbr, roster, depthChart, onSave, onClose }) => {
  // Initialize from saved or auto-generate
  const defaultChart = useMemo(() => generateDefaultDepthChart(roster), [roster]);
  const [chart, setChart] = useState(() => {
    if (depthChart?.positions) return JSON.parse(JSON.stringify(depthChart));
    return defaultChart ? JSON.parse(JSON.stringify(defaultChart)) : { positions: { PG: [], SG: [], SF: [], PF: [], C: [] }, dnp: [] };
  });

  const [dragSource, setDragSource] = useState(null); // { pos, idx } or { dnp: true, name }
  const [swapTarget, setSwapTarget] = useState(null);

  // All assigned player names
  const assignedNames = useMemo(() => {
    const names = new Set();
    for (const pos of POSITIONS) {
      for (const slot of (chart.positions[pos] || [])) {
        if (slot?.name) names.add(slot.name);
      }
    }
    return names;
  }, [chart]);

  // Available players (not assigned to any position)
  const availablePlayers = useMemo(() => {
    const dnpSet = new Set(chart.dnp || []);
    return roster
      .filter(p => !p.isDead && (p.salary2526 || 0) > 0 && !assignedNames.has(p.name) && !dnpSet.has(p.name))
      .sort((a, b) => (b.ovr || getPlayerRating(b.name) || 70) - (a.ovr || getPlayerRating(a.name) || 70));
  }, [roster, assignedNames, chart.dnp]);

  // Total minutes
  const totalMinutes = useMemo(() => {
    let sum = 0;
    for (const pos of POSITIONS) {
      for (const slot of (chart.positions[pos] || [])) {
        sum += slot?.minutes || 0;
      }
    }
    return sum;
  }, [chart]);

  // Swap two players
  const swapPlayers = useCallback((sourcePos, sourceIdx, targetPos, targetIdx) => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const srcSlot = next.positions[sourcePos]?.[sourceIdx];
      const tgtSlot = next.positions[targetPos]?.[targetIdx];
      if (srcSlot && tgtSlot) {
        // Swap names but keep minutes
        const tmpName = srcSlot.name;
        srcSlot.name = tgtSlot.name;
        tgtSlot.name = tmpName;
      }
      return next;
    });
  }, []);

  // Assign player from DNP/available to a slot
  const assignPlayer = useCallback((playerName, pos, idx) => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      // Remove player from any current slot
      for (const p of POSITIONS) {
        for (const slot of (next.positions[p] || [])) {
          if (slot.name === playerName) slot.name = '';
        }
      }
      next.dnp = (next.dnp || []).filter(n => n !== playerName);
      // Place in target
      if (!next.positions[pos]) next.positions[pos] = [];
      while (next.positions[pos].length <= idx) next.positions[pos].push({ name: '', minutes: 0 });
      const displaced = next.positions[pos][idx]?.name;
      next.positions[pos][idx] = { ...next.positions[pos][idx], name: playerName };
      // If someone was displaced, add to DNP
      if (displaced && displaced !== playerName) {
        next.dnp = [...(next.dnp || []), displaced];
      }
      return next;
    });
  }, []);

  // Remove player to DNP
  const removePlayer = useCallback((pos, idx) => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const slot = next.positions[pos]?.[idx];
      if (slot?.name) {
        slot.minutes = 0; // Set to 0 minutes (DNP) instead of removing
      }
      return next;
    });
  }, []);

  // Update minutes
  const updateMinutes = useCallback((pos, idx, minutes) => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (next.positions[pos]?.[idx]) {
        next.positions[pos][idx].minutes = Math.max(0, Math.min(48, minutes));
      }
      return next;
    });
  }, []);

  // Add a slot to a position
  const addSlot = useCallback((pos) => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      if (!next.positions[pos]) next.positions[pos] = [];
      if (next.positions[pos].length < 4) {
        next.positions[pos].push({ name: '', minutes: 0 });
      }
      return next;
    });
  }, []);

  // Auto-balance minutes to 240
  const autoBalance = useCallback(() => {
    setChart(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const allSlots = [];
      for (const pos of POSITIONS) {
        for (let i = 0; i < (next.positions[pos] || []).length; i++) {
          const slot = next.positions[pos][i];
          if (slot?.name) allSlots.push({ pos, idx: i, slot });
        }
      }
      if (allSlots.length === 0) return next;

      // Count how many players at each depth level
      const starters = allSlots.filter(s => s.idx === 0);
      const backups = allSlots.filter(s => s.idx === 1);
      const reserves = allSlots.filter(s => s.idx >= 2);

      // Target: 240 total minutes
      // Distribute proportionally: starters get most, reserves least
      const nS = starters.length || 1;
      const nB = backups.length;
      const nR = reserves.length;

      // Base per-player: starters share ~200 mins, backups ~35, reserves ~5
      const starterMin = nS > 0 ? Math.round(200 / nS) : 0;
      const backupMin = nB > 0 ? Math.round(35 / nB) : 0;
      const reserveMin = nR > 0 ? Math.round(5 / nR) : 0;

      // Better approach: fixed per-depth targets
      for (const s of starters) s.slot.minutes = Math.min(38, Math.max(26, Math.round(240 / (nS + nB * 0.5 + nR * 0.15))));
      for (const s of backups) s.slot.minutes = Math.round(starters[0]?.slot.minutes * 0.55 || 16);
      for (const s of reserves) s.slot.minutes = Math.round(starters[0]?.slot.minutes * 0.22 || 6);

      // Fine-tune to exactly 240
      let total = allSlots.reduce((s, x) => s + x.slot.minutes, 0);
      // Sort by OVR descending — adjust highest OVR players first
      const sorted = [...allSlots].sort((a, b) => (getPlayerRating(b.slot.name) || 70) - (getPlayerRating(a.slot.name) || 70));
      let idx = 0;
      while (total !== 240 && idx < 500) {
        const s = sorted[idx % sorted.length];
        if (total > 240 && s.slot.minutes > (s.idx >= 2 ? 0 : s.idx === 1 ? 8 : 20)) {
          s.slot.minutes--;
          total--;
        } else if (total < 240 && s.slot.minutes < (s.idx === 0 ? 40 : s.idx === 1 ? 28 : 18)) {
          s.slot.minutes++;
          total++;
        }
        idx++;
      }
      return next;
    });
  }, []);

  // Reset to auto-generated
  const resetChart = useCallback(() => {
    const fresh = generateDefaultDepthChart(roster);
    if (fresh) setChart(JSON.parse(JSON.stringify(fresh)));
  }, [roster]);

  const handleSave = useCallback(() => {
    onSave(teamAbbr, chart);
  }, [onSave, teamAbbr, chart]);

  // ---- RENDER ----
  const minutesColor = totalMinutes === 240 ? 'var(--accent-green)' : totalMinutes > 240 ? 'var(--accent-red)' : 'var(--accent-gold)';

  return (
    <div className="depth-chart fade-in">
      <div className="depth-chart-header">
        <div>
          <div className="fa-board-label">Depth Chart</div>
          <div className="fa-board-title" style={{ fontSize: 20 }}>Starting Lineup & Minutes</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: minutesColor, fontWeight: 700 }}>
            {totalMinutes}/240 min
          </span>
          <button className="fa-back-btn" onClick={autoBalance}>Auto-Balance</button>
          <button className="fa-back-btn" onClick={resetChart}>Reset</button>
          <button className="fa-skip-btn" onClick={handleSave}>Save</button>
        </div>
      </div>

      <div className="depth-chart-grid">
        {POSITIONS.map(pos => (
          <div key={pos} className="depth-chart-position">
            <div className="depth-chart-pos-header">{pos}</div>
            {(chart.positions[pos] || []).map((slot, idx) => {
              const playerName = slot?.name;
              const naturalPos = playerName ? (PLAYER_POSITIONS[playerName] || 'SF') : '';
              const baseOvr = playerName ? (getPlayerRating(playerName) || 70) : 0;
              const penalty = playerName ? getOOPPenalty(naturalPos, pos) : 0;
              const effectiveOvr = baseOvr + penalty;
              const rColor = getRatingColor(effectiveOvr);
              const isOOP = penalty < 0;
              const isSelected = dragSource?.pos === pos && dragSource?.idx === idx;
              const isTarget = swapTarget?.pos === pos && swapTarget?.idx === idx;

              return (
                <div
                  key={idx}
                  className={`depth-chart-slot ${idx === 0 ? 'starter' : ''} ${isSelected ? 'selected' : ''} ${isTarget ? 'swap-target' : ''}`}
                  onClick={() => {
                    if (!dragSource) {
                      // Nothing selected yet — select this slot
                      if (playerName) {
                        setDragSource({ pos, idx });
                      }
                    } else if (isSelected) {
                      // Clicked same slot — deselect
                      setDragSource(null);
                      setSwapTarget(null);
                    } else if (dragSource.dnp) {
                      // Assigning from available pool
                      assignPlayer(dragSource.name, pos, idx);
                      setDragSource(null);
                      setSwapTarget(null);
                    } else {
                      // Second click — swap the two players
                      swapPlayers(dragSource.pos, dragSource.idx, pos, idx);
                      setDragSource(null);
                      setSwapTarget(null);
                    }
                  }}
                  onMouseEnter={() => dragSource && !isSelected && setSwapTarget({ pos, idx })}
                  onMouseLeave={() => setSwapTarget(null)}
                >
                  <div className="depth-chart-slot-label">{DEPTH_LABELS[idx] || `Depth ${idx + 1}`}</div>
                  {playerName ? (
                    <div className="depth-chart-player">
                      <div className="depth-chart-player-info">
                        <span className="ovr-badge" style={{ borderLeftColor: rColor, fontSize: 11, padding: '2px 6px' }}>
                          {effectiveOvr}
                        </span>
                        <div>
                          <div className="depth-chart-player-name">{playerName}</div>
                          <div className="depth-chart-player-pos">
                            {naturalPos}
                            {isOOP && <span className="depth-chart-oop">{penalty} OVR</span>}
                          </div>
                        </div>
                      </div>
                      <div className="depth-chart-player-actions">
                        <div className="depth-chart-minutes">
                          <button className="depth-chart-min-btn" onClick={(e) => { e.stopPropagation(); updateMinutes(pos, idx, (slot.minutes || 0) - 1); }}>−</button>
                          <input
                            className="depth-chart-min-input"
                            type="number"
                            value={slot.minutes || 0}
                            onChange={e => updateMinutes(pos, idx, parseInt(e.target.value) || 0)}
                            onClick={e => e.stopPropagation()}
                            min={0}
                            max={48}
                          />
                          <button className="depth-chart-min-btn" onClick={(e) => { e.stopPropagation(); updateMinutes(pos, idx, (slot.minutes || 0) + 1); }}>+</button>
                        </div>
                        <button className="depth-chart-remove-btn" title="Remove to bench" onClick={(e) => { e.stopPropagation(); removePlayer(pos, idx); }}>×</button>
                      </div>
                    </div>
                  ) : (
                    <div className="depth-chart-empty">
                      <span>{dragSource ? 'Click to place here' : 'Empty'}</span>
                    </div>
                  )}
                </div>
              );
            })}
            {(chart.positions[pos] || []).length < 4 && (
              <button className="depth-chart-add-slot" onClick={() => addSlot(pos)}>+ Add Slot</button>
            )}
          </div>
        ))}
      </div>

      {/* Available / DNP Players */}
      <div className="depth-chart-available">
        <div className="depth-chart-avail-header">
          Available Players ({availablePlayers.length + (chart.dnp || []).length})
          {dragSource && <span style={{ fontSize: 10, color: 'var(--accent-gold)', marginLeft: 8 }}>{dragSource.dnp ? 'Click a slot to place' : 'Click another player to swap'}</span>}
        </div>
        <div className="depth-chart-avail-list">
          {[...(chart.dnp || []).map(name => roster.find(p => p.name === name)).filter(Boolean), ...availablePlayers].map(p => {
            const name = p.name;
            const ovr = p.ovr || getPlayerRating(name) || 70;
            const naturalPos = PLAYER_POSITIONS[name] || 'SF';
            const rColor = getRatingColor(ovr);
            const isSelected = dragSource?.dnp && dragSource?.name === name;
            return (
              <div
                key={name}
                className={`depth-chart-avail-player ${isSelected ? 'selected' : ''}`}
                onClick={() => {
                  if (isSelected) {
                    setDragSource(null);
                  } else {
                    setDragSource({ dnp: true, name });
                  }
                }}
              >
                <span className="ovr-badge" style={{ borderLeftColor: rColor, fontSize: 10, padding: '1px 5px' }}>{ovr}</span>
                <span className="depth-chart-avail-name">{name}</span>
                <span className="badge badge-muted" style={{ fontSize: 8 }}>{naturalPos}</span>
                {p.isTwoWay && <span className="badge badge-muted" style={{ fontSize: 7 }}>2W</span>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DepthChart;
